// Shared react-simple-maps scaffolding for every India choropleth (the sales home
// map and the MIS Business Analytics map). One projection config and one
// state-name normalization entry point, so a fix to either lands in a single place
// instead of drifting between copies.

import indiaGeo from '@/assets/india-states.geo.json';
import { normalizeStateName } from '@/utils/indiaStates';

// geoMercator config framing mainland India plus the island territories.
export const INDIA_PROJECTION_CONFIG = { scale: 950, center: [82.8, 22.5] as [number, number] };

export interface GeographyShape {
  rsmKey: string;
  properties: Record<string, unknown>;
}

// Coerce a GeoJSON ST_NM value to the canonical state name used everywhere else.
// Delegates to utils/indiaStates.normalizeStateName - the single, case-insensitive
// source of truth for spelling/abbreviation variants (orissa, uttaranchal, j&k,
// NCT of Delhi, ...) - so the map and the rest of the app coerce names identically
// and a new mapping only has to be added in one place.
export const normalizeName = (n: string): string => normalizeStateName(n);

// Canonical state name for a rendered geography's properties (reads ST_NM, falling
// back to NAME_1). Used by both India maps so the raw-property -> canonical-name
// step is not duplicated in each render loop.
export const geoStateName = (properties: Record<string, unknown>): string => {
  const stNm = typeof properties.ST_NM === 'string' ? properties.ST_NM : '';
  const name1 = typeof properties.NAME_1 === 'string' ? properties.NAME_1 : '';
  return normalizeName(stNm || name1);
};

interface GeoFeature {
  properties?: Record<string, unknown>;
}

interface GeoCollection {
  features?: GeoFeature[];
}

// Canonical names of every state the bundled GeoJSON actually renders. A choropleth
// scopes its colour-scale maximum to these, so an off-map aggregate bucket (e.g. an
// "Others" row, or any name absent from the GeoJSON) can't set an invisible ceiling
// that washes every visible state out to a near-empty tint.
export const INDIA_GEO_STATE_NAMES: ReadonlySet<string> = new Set(
  ((indiaGeo as GeoCollection).features ?? []).map((f) => geoStateName(f.properties ?? {})),
);
